import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/query-keys';
import type {
  EnterpriseCapability,
  EnterpriseCapabilityPartner,
} from '@/lib/enterprise-capabilities';

/**
 * M1 (#571): the caller's effective enterprise capabilities, per partner.
 * Calls `my_enterprise_capabilities()` — for each partner the caller touches it
 * returns `{ partner_id, name, is_admin, capabilities }` where a full partner
 * admin / super_admin implicitly holds every capability (`is_admin: true`).
 *
 * Cosmetic gate only — NOT a security boundary. Server RPCs recompute
 * authorization. `useIsEnterpriseAdmin` is derived from this hook.
 */
export function useEnterpriseCapabilities() {
  const { user } = useAuth();

  const { data = [], isPending: isLoading } = useQuery({
    queryKey: queryKeys.enterprisePartners.myCapabilities(user?.id ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('my_enterprise_capabilities');
      if (error) throw error;
      return (data ?? []) as EnterpriseCapabilityPartner[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const hasCapability = (partnerId: string, cap: EnterpriseCapability) =>
    data.some((p) => p.partner_id === partnerId && (p.is_admin || p.capabilities.includes(cap)));

  return {
    partners: data,
    isLoading,
    hasAnyEnterpriseCapability: data.length > 0,
    hasCapability,
  };
}
